import { useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import {
  getWhatsAppWebQR,
  getWhatsAppWebStatus,
} from "../../../../services/api";

/**
 * Modal de QR Code com polling de status.
 * Replica a UX do OrbitSender: loading → QR + contador "Atualiza em Xs" →
 * fecha automaticamente quando canal conecta (status 'active').
 */
export default function QRModal({ show, channel, onClose, onConnected }) {
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState("loading");
  const [countdown, setCountdown] = useState(5);
  const intervalRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!show || !channel?.id) return undefined;

    let cancelled = false;
    setQr(null);
    setStatus("loading");
    setCountdown(5);

    async function pollOnce() {
      try {
        const [qrRes, statusRes] = await Promise.all([
          getWhatsAppWebQR(channel.id),
          getWhatsAppWebStatus(channel.id),
        ]);
        if (cancelled) return;
        const newStatus = statusRes?.data?.status;
        setStatus(newStatus || "unknown");
        if (newStatus === "active") {
          onConnected?.(channel);
          return;
        }
        setQr(qrRes?.data?.qr || null);
        setCountdown(5);
      } catch (err) {
        if (!cancelled) setStatus("error");
      }
    }

    pollOnce();
    intervalRef.current = setInterval(pollOnce, 5000);
    tickRef.current = setInterval(
      () => setCountdown((c) => (c > 0 ? c - 1 : 0)),
      1000
    );

    return () => {
      cancelled = true;
      clearInterval(intervalRef.current);
      clearInterval(tickRef.current);
    };
  }, [show, channel, onConnected]);

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>💬 Conexão QR Code</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {status === "loading" && !qr && (
          <>
            <Spinner animation="border" variant="success" />
            <h5 className="text-success mt-3">Buscando QR Code...</h5>
            <p className="text-muted">Aguarde alguns segundos...</p>
          </>
        )}

        {qr && status !== "active" && (
          <>
            <h5 className="text-success">Basta se Conectar!</h5>
            <img
              src={qr}
              alt="QR Code"
              style={{
                maxWidth: 300,
                background: "#fff",
                padding: 8,
                borderRadius: 8,
              }}
            />
            <p className="mt-3 text-muted">
              <Spinner size="sm" animation="border" /> Atualiza em{" "}
              <strong>{countdown}s</strong>
            </p>
            <div
              className="text-left p-3 mt-3"
              style={{
                background: "rgba(255,255,255,0.05)",
                borderRadius: 8,
              }}
            >
              <strong>Passo a passo para Conectar:</strong>
              <ol className="mb-0 mt-2 pl-3">
                <li>Abra o WhatsApp no seu celular.</li>
                <li>
                  Toque em mais opções (3 pontinhos) no Android, ou em
                  configurações (Engrenagem) no iPhone.
                </li>
                <li>
                  Toque em "Dispositivos conectados" e, em seguida, em
                  "Conectar dispositivo".
                </li>
                <li>Escaneie o QR code para conectar!</li>
              </ol>
            </div>
          </>
        )}

        {status === "active" && (
          <>
            <div style={{ fontSize: 64 }}>✅</div>
            <h5 className="text-success">Conectado!</h5>
            <p className="text-muted">Canal pronto para uso.</p>
          </>
        )}

        {status === "error" && (
          <p className="text-danger">
            Erro ao buscar QR. Tente novamente em alguns segundos.
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
}
